import React, { useEffect, useRef, useState } from 'react';
import { ClassNames, ListBoxRenderer } from 'react-tag-autocomplete';
import SimpleBar from 'simplebar-react';

// hooks
import useContentUGCPermissions from './useContentUGCPermissions';
import { BasicCheckbox, BoldTextButton, LoadingSpinner } from 'thrive-components';
//components
import AutocompleteWithTags from '../global/textInputs/AutocompleteWithTags';
import UserConfirmationModal from  '@/components/modals/ConfirmationModal';
import ModalWrapper from  '@/components/modals/ModalWrapper';
import LoadingCircle from '../global/LoadingCircle/LoadingCircle';

//helpers and services
import { simpleBarAutoScrollHandler } from  '@/helpers/SimpleBar';

//interfaces
import { IContentUGCPermissions } from  '@/interfaces/contentUGCPermissions';

import  '@/css/content/ugcpermissions.scss';


/* This only exists here, so not abstracting into its own file, just taking it out of render cycles*/
const CustomListWrapper = (simpleBarRef:React.MutableRefObject<undefined> , shouldFetchRef: React.MutableRefObject<boolean>, getNextPage: () => void, isLoading: boolean) => {
    return ({ children, classNames, ...listBoxProps }: {children: React.ReactNode, classNames: ClassNames}) => (
    
        <div className={`${classNames.listBox} listbox-custom` } {...listBoxProps}>
            <SimpleBar 
                style={{maxHeight: '12.5rem' }}
                scrollableNodeProps={{ 
                    ref: simpleBarRef,
                    onScroll: (e: React.UIEvent<HTMLElement>) => simpleBarAutoScrollHandler(e, shouldFetchRef.current, getNextPage)
                }}
            >
                {children}
            </SimpleBar>
            {isLoading && 
                <div style={{position: 'absolute', bottom: '5px', zIndex:999, right:'10px'}}>
                    <LoadingCircle color='grey'/>
                </div>
            }
    </div>)
}


const ContentUGCPermissions = ({groupsAllowedToPost}: IContentUGCPermissions) => {

    const { 
        contentId,
        selected,
        setSelected,
        enableRestrictedPosts,
        setCheckboxToggle,
        isLoading,
        suggestions,
        setSuggestions,
        input,
        setInput,
        showModal,
        setShowModal,
        setCheckboxAndClearInputs,
        getSuggestions, 
        getNextPage,
        moreAvailable,
    } = useContentUGCPermissions({groupsAllowedToPost});

    const simpleBarRef = useRef()
    const shouldFetchRef = useRef<boolean>(false);
    const [isSaving, setIsSaving] = useState<boolean>(false)

    useEffect(() => {
        shouldFetchRef.current = moreAvailable && !isLoading
    }, [moreAvailable, isLoading])
   
    return (
        <section className='content-audience-ugc-permissions-wrapper'>
            <div className='ugc-restrict-checkbox-wrapper'>
                <BasicCheckbox 
                    id={'toggle-who-can-post'}
                    isChecked={enableRestrictedPosts}
                    onChange={() => setCheckboxToggle(true)}
                    label={'Restrict who can post to this list'} 
                    ariaLabel={'Restrict who can post to this list'}
                />
            </div>
            
            {(!Number(contentId) && enableRestrictedPosts) && 
                <div className='save-this-list-wrapper'>
                    <BoldTextButton
                        ariaLabel={'Save this item'}
                        text={'Save this item'}
                        isDisabled={isSaving}
                        onButtonClick={async () => {
                            if((window as any).formIsValid(true)) {
                                setIsSaving(true);
                                await (window as any)?.SaveContent(false)
                                setIsSaving(false)
                            }
                            else {
                                 (window as any).functions.validateForm()
                                 window.scrollTo({
                                    top: 0,
                                    left: 0,
                                    behavior: "smooth",
                                })
                            }
                           
                        }}
                    />
                    <span>to restrict users</span>
                    {isSaving && <LoadingSpinner size={12} />}
                </div>
            }
            
            
            <div className='auto-complete-tags-ugc-perms-wrapper'>
                <div className='ugc-label'>GROUPS THAT CAN POST</div>

                <AutocompleteWithTags 
                    id={"groups-auto-complete"}
                    selected={selected}
                    setSelected={setSelected}
                    suggestions={suggestions}
                    setSuggestions={setSuggestions}
                    noOptionsText={isLoading ? "searching.." : `No matching groups for ${input}`}
                    placeholderText="Search by group name"
                    input={input}
                    setInput={setInput}
                    isDisabled={!(Number(contentId) && enableRestrictedPosts)}
                    customOnAddCb={() => getSuggestions('')}
                    customListBox={CustomListWrapper(simpleBarRef, shouldFetchRef, getNextPage, isLoading) as ListBoxRenderer}
                />   
            </div>
            
            
            <ModalWrapper isOpen={showModal} onRequestClose={()=> {setShowModal(false)}} className='clear-groups-that-can-post-modal-container'>
                <UserConfirmationModal
                    confirmationModalVariables={{
                        modalTitle: 'Allow anyone to post?',
                        confirmationQuestion: 'Turning off this restriction will enable all audience members to post to this list',
                        leftButton: 'Cancel',
                        rightButton: 'Continue'
                    }}
                    onLeftButtonClick={()=> setShowModal(false)}
                    onRightButtonClick={ setCheckboxAndClearInputs }
                    modalClassName={'clear-groups-that-can-post-modal'}
                />
            </ModalWrapper>
                
        </section>
    )

}

export default ContentUGCPermissions;


